/**
 * Default product template (PageFly) Length/Grip inject + linked ATC.
 * Bootstrapped only when theme.liquid outputs window.BGCPageflyOptionConfig.
 *
 * Pricing: choices need only label + price (no per-choice variant_id).
 * Paid options resolve a fee variant by exact price via:
 *   1) pricingMap.price_variants / config.priceVariants  { "14.99": 123, "1499": 123 }
 *   2) pricingMap.fee_product / config.feeProductHandle  product whose variants match price
 *   3) optional legacy data-addon-variant-id on the option (still supported)
 */
(function () {
  const config = window.BGCPageflyOptionConfig;
  if (!config || !config.pricingMap || !config.productId) return;
  if (window.__BGCPageflyOptionAddonsInit) return;
  window.__BGCPageflyOptionAddonsInit = true;

  const feeProductCache = {};

  function normalizeGroups(pricingMap) {
    if (!pricingMap) return [];
    if (Array.isArray(pricingMap.groups)) {
      return pricingMap.groups.filter((g) => g && Array.isArray(g.choices) && g.choices.length);
    }

    const groups = [];
    const lengthGroup = pricingMap.club_length || pricingMap.Length || pricingMap.length;
    const gripGroup = pricingMap.grip || pricingMap.Grip;
    if (lengthGroup && Array.isArray(lengthGroup.choices)) {
      groups.push({ key: 'length', ...lengthGroup });
    }
    if (gripGroup && Array.isArray(gripGroup.choices)) {
      groups.push({ key: 'grip', ...gripGroup });
    }
    return groups;
  }

  function choiceLabel(choice) {
    return choice.label || choice.value || choice.name || '';
  }

  function choicePrice(choice) {
    const price = parseFloat(choice.price);
    return Number.isNaN(price) ? 0 : price;
  }

  function toCents(price) {
    return Math.round(Number(price) * 100);
  }

  function findHulkMount(productId) {
    return (
      document.getElementById('hulkapps_custom_options_' + productId) ||
      document.querySelector('[id^="hulkapps_custom_options_"]') ||
      null
    );
  }

  function findInsertAnchor(productId) {
    const hulkMount = findHulkMount(productId);
    if (hulkMount) return hulkMount;

    return (
      document.querySelector('[data-pf-type="ProductPrice2"]')?.parentElement ||
      document.querySelector('.pf-product-form [data-pf-type="Block"]') ||
      document.querySelector('.pf-product-form') ||
      null
    );
  }

  function hideHulkOptions(productId) {
    document.documentElement.classList.add('bgc-native-options-active');

    const hulkMount = findHulkMount(productId);
    if (hulkMount) {
      hulkMount.setAttribute('data-bgc-hulk-hidden', 'true');
      hulkMount.setAttribute('aria-hidden', 'true');
      hulkMount.hidden = true;
      hulkMount.style.setProperty('display', 'none', 'important');
    }

    const scope =
      document.querySelector('.pf-product-form') ||
      document.querySelector('[data-pf-type="ProductBox"]') ||
      document;

    scope
      .querySelectorAll(
        '.hulkapps-option, .hulkapps_option, .hulk-po-option, .hulkapps_option_set, [class*="hulkapps-option"], [id*="hulkapps_option"]:not([id^="hulkapps_custom_options_"])'
      )
      .forEach((el) => {
        if (el.closest('[data-bgc-source="pagefly"]')) return;
        el.setAttribute('data-bgc-hulk-hidden', 'true');
        el.setAttribute('aria-hidden', 'true');
        el.style.setProperty('display', 'none', 'important');
      });
  }

  function watchHulkRerender(productId) {
    const root = document.querySelector('.pf-product-form') || document.body;
    if (!root || window.__BGCHulkHideObserver) return;

    window.__BGCHulkHideObserver = new MutationObserver(function () {
      hideHulkOptions(productId);
    });

    window.__BGCHulkHideObserver.observe(root, {
      childList: true,
      subtree: true,
    });
  }

  function formatMoneyFromCents(cents) {
    const value = Number(cents) || 0;
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      try {
        return Shopify.formatMoney(value, config.moneyFormat || undefined);
      } catch (e) {
        /* fall through */
      }
    }
    return '$' + (value / 100).toFixed(2);
  }

  function formatMoneyFromDollars(dollars) {
    return formatMoneyFromCents(toCents(dollars));
  }

  function getBasePriceCents() {
    // Prefer live PageFly selected variant (tracks option changes)
    const pfProduct = window.__pageflyProducts && window.__pageflyProducts[config.productId];
    const liveVariant =
      pfProduct?.selected_or_first_available_variant ||
      pfProduct?.selected_variant ||
      null;
    if (liveVariant && typeof liveVariant.price === 'number') {
      return Math.round(liveVariant.price);
    }

    // Fallback: read PageFly displayed price if present
    const pfPrice = document.querySelector(
      '[data-pf-type="ProductPrice2Item"][data-product-price="true"], [data-product-type="price"]'
    );
    if (pfPrice) {
      const raw = (pfPrice.textContent || '').replace(/[^0-9.]/g, '');
      const dollars = parseFloat(raw);
      if (!Number.isNaN(dollars)) return toCents(dollars);
    }

    if (typeof config.basePriceCents === 'number') return config.basePriceCents;
    return 0;
  }

  function buildPickerHtml(groups, productId, productTitle) {
    const groupHtml = groups
      .map((group, index) => {
        const key = group.key || 'option-' + index;
        const label = group.label || key;
        const property = group.property || key;
        const options = (group.choices || [])
          .map((choice) => {
            const labelText = choiceLabel(choice);
            const price = choicePrice(choice);
            const variantId = choice.variant_id || choice.variantId || '';
            const text = price > 0 ? `${labelText} — ${formatMoneyFromDollars(price)}` : labelText;
            const variantAttr = variantId ? ` data-addon-variant-id="${variantId}"` : '';
            return `<option value="${escapeAttr(labelText)}" data-addon-price="${price}"${variantAttr}>${escapeHtml(
              text
            )}</option>`;
          })
          .join('');

        return `
          <fieldset class="js product-form__input product-form__input--dropdown bgc-option-addons__group">
            <legend class="form__label">${escapeHtml(label)}</legend>
            <div class="select">
              <select
                class="select__select custom-price-select bgc-addon-select"
                id="BGC-Pagefly-Addon-${productId}-${escapeAttr(key)}"
                data-bgc-group="${escapeAttr(key)}"
                data-bgc-property="${escapeAttr(property)}"
                data-bgc-label="${escapeAttr(label)}"
                aria-label="${escapeAttr(label)}"
              >${options}</select>
            </div>
          </fieldset>`;
      })
      .join('');

    const baseLabel = formatMoneyFromCents(getBasePriceCents());

    return `
      <div
        class="bgc-option-addons bgc-option-addons--pagefly custom-variant"
        data-bgc-option-addons
        data-bgc-source="pagefly"
        data-product-title="${escapeAttr(productTitle)}"
        data-product-id="${escapeAttr(String(productId))}"
      >
        ${groupHtml}
        <div class="bgc-live-total" aria-live="polite">
          <span class="bgc-live-total__label">Total</span>
          <span class="bgc-live-total__price" id="bgc-total-price">${escapeHtml(baseLabel)}</span>
        </div>
        <div class="price-optin-total bgc-addon-subline">
          Options add
          <span id="extra-price">$0.00</span>
        </div>
      </div>`;
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function escapeAttr(value) {
    return escapeHtml(value).replace(/'/g, '&#39;');
  }

  function getAddonSelects() {
    return Array.from(document.querySelectorAll('[data-bgc-source="pagefly"] .bgc-addon-select'));
  }

  function getSelectedAddonDollars() {
    let total = 0;
    getAddonSelects().forEach((select) => {
      const option = select.options[select.selectedIndex];
      if (!option) return;
      const price = parseFloat(option.getAttribute('data-addon-price') || '0');
      if (!Number.isNaN(price)) total += price;
    });
    return total;
  }

  function updateExtraPrice() {
    const addonDollars = getSelectedAddonDollars();
    const extra =
      document.querySelector('[data-bgc-source="pagefly"] #extra-price') || document.getElementById('extra-price');
    if (extra) {
      extra.textContent = formatMoneyFromDollars(addonDollars);
    }

    const totalEl =
      document.querySelector('[data-bgc-source="pagefly"] #bgc-total-price') ||
      document.getElementById('bgc-total-price');
    if (totalEl) {
      const totalCents = getBasePriceCents() + toCents(addonDollars);
      totalEl.textContent = formatMoneyFromCents(totalCents);
    }

    const subline = document.querySelector('[data-bgc-source="pagefly"] .bgc-addon-subline');
    if (subline) {
      subline.hidden = addonDollars <= 0;
    }
  }

  function buildParentProperties(linkId) {
    const properties = {};
    if (linkId) properties._bgc_link_id = linkId;
    getAddonSelects().forEach((select) => {
      const option = select.options[select.selectedIndex];
      if (!option) return;
      const price = parseFloat(option.getAttribute('data-addon-price') || '0');
      const propertyName = select.getAttribute('data-bgc-property');
      if (!propertyName || !(price > 0)) return;
      properties[propertyName] = `${option.value} (+$${price.toFixed(2)})`;
    });
    return properties;
  }

  function lookupPriceVariantMap(price) {
    const cents = toCents(price);
    const maps = [config.pricingMap?.price_variants, config.priceVariants].filter(Boolean);

    for (const map of maps) {
      for (const [key, id] of Object.entries(map)) {
        if (!id && id !== 0) continue;
        const keyNum = Number(key);
        const keyCents = Number.isNaN(keyNum)
          ? null
          : String(key).includes('.')
            ? toCents(keyNum)
            : keyNum > 999
              ? Math.round(keyNum)
              : toCents(keyNum);
        // Accept keys as dollars ("14.99"), cents ("1499"), or dollar integers ("15")
        const candidates = [toCents(keyNum), Math.round(keyNum), keyNum];
        if (candidates.includes(cents) || keyCents === cents) {
          return Number(id);
        }
      }
    }
    return null;
  }

  async function loadFeeProductVariants(handle) {
    if (!handle) return [];
    if (feeProductCache[handle]) return feeProductCache[handle];

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const response = await fetch(`${root}products/${handle}.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) {
      feeProductCache[handle] = [];
      return [];
    }
    const product = await response.json();
    feeProductCache[handle] = Array.isArray(product.variants) ? product.variants : [];
    return feeProductCache[handle];
  }

  async function resolveFeeVariantId(price, explicitVariantId) {
    if (explicitVariantId) return Number(explicitVariantId);

    const cents = toCents(price);
    if (!(cents > 0)) return null;

    const mapped = lookupPriceVariantMap(price);
    if (mapped) return mapped;

    const handle = config.pricingMap?.fee_product || config.feeProductHandle;
    if (!handle) return null;

    const variants = await loadFeeProductVariants(handle);
    // product.js prices are in cents — exact match only
    const match = variants.find((variant) => Number(variant.price) === cents && variant.available !== false);
    if (match) return Number(match.id);

    // Allow unavailable match as last resort (inventory policy may still allow)
    const anyMatch = variants.find((variant) => Number(variant.price) === cents);
    return anyMatch ? Number(anyMatch.id) : null;
  }

  async function getSelectedAddonItems(parentTitle, linkId) {
    const items = [];
    const unresolved = [];

    for (const select of getAddonSelects()) {
      const option = select.options[select.selectedIndex];
      if (!option) continue;

      const price = parseFloat(option.getAttribute('data-addon-price') || '0');
      if (!(price > 0)) continue;

      const explicitId = option.getAttribute('data-addon-variant-id');
      const variantId = await resolveFeeVariantId(price, explicitId);
      const propertyName = select.getAttribute('data-bgc-property') || 'Option';
      const groupLabel = select.getAttribute('data-bgc-label') || propertyName;

      if (!variantId) {
        unresolved.push(`${groupLabel}: ${option.value} (+$${price.toFixed(2)})`);
        continue;
      }

      const properties = {
        _bgc_linked: 'true',
        _bgc_for: parentTitle || '',
        _bgc_addon_price: String(price),
      };
      if (linkId) properties._bgc_link_id = linkId;
      properties[propertyName] = option.value;
      properties.Option = `${groupLabel}: ${option.value}`;

      items.push({
        id: Number(variantId),
        quantity: 1,
        properties,
      });
    }

    return { items, unresolved };
  }

  function resolveVariantId() {
    const pfProduct = window.__pageflyProducts && window.__pageflyProducts[config.productId];
    return (
      pfProduct?.selected_or_first_available_variant?.id ||
      pfProduct?.selected_variant?.id ||
      pfProduct?.variants?.[0]?.id ||
      config.variantId
    );
  }

  function resolveQuantity() {
    const input =
      document.querySelector('.pf-product-form [data-pf-type="QuantityField"]') ||
      document.querySelector('.pf-product-form input[name="quantity"]') ||
      document.querySelector('[data-pf-type="QuantityField"]');
    const qty = Number(input && input.value ? input.value : 1);
    return qty > 0 ? qty : 1;
  }

  function getCartDrawer() {
    return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
  }

  function cartAddUrl() {
    // Use theme route (/cart/add) so section HTML is returned like Dawn product-form
    return window.routes?.cart_add_url || '/cart/add';
  }

  function showAtcMessage(button, message, isError) {
    let note = document.getElementById('bgc-pagefly-atc-message');
    if (!note) {
      note = document.createElement('p');
      note.id = 'bgc-pagefly-atc-message';
      note.setAttribute('role', 'alert');
      note.style.margin = '8px 0 0';
      note.style.fontSize = '14px';
      const mount =
        document.querySelector('[data-bgc-source="pagefly"]') ||
        button.parentElement ||
        document.querySelector('.pf-product-form');
      if (mount) mount.appendChild(note);
    }
    note.textContent = message || '';
    note.style.color = isError ? '#8a1f1f' : '#1f5c38';
    note.hidden = !message;
  }

  function appendItemToFormData(formData, item) {
    formData.append('id', String(item.id));
    formData.append('quantity', String(item.quantity || 1));
    Object.entries(item.properties || {}).forEach(([key, value]) => {
      formData.append(`properties[${key}]`, value);
    });
  }

  async function addItemWithOptionalSections(item, withSections) {
    const formData = new FormData();
    appendItemToFormData(formData, item);

    const cart = getCartDrawer();
    if (withSections && cart && typeof cart.getSectionsToRender === 'function') {
      formData.append(
        'sections',
        cart.getSectionsToRender().map((section) => section.id)
      );
      formData.append('sections_url', window.location.pathname);
      if (typeof cart.setActiveElement === 'function') {
        cart.setActiveElement(document.activeElement);
      }
    }

    const response = await fetch(cartAddUrl(), {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/javascript',
      },
      body: formData,
    });
    const data = await response.json().catch(() => ({}));
    return { response, data };
  }

  async function fetchCartSections(sectionIds) {
    if (!sectionIds.length) return null;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const url = `${root}?sections=${encodeURIComponent(sectionIds.join(','))}`;
    const response = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!response.ok) return null;
    return response.json();
  }

  function renderCartLikeDawn(cart, parsedState) {
    if (!cart) return false;

    try {
      cart.classList.remove('is-empty');
      const itemsEl = cart.querySelector('cart-drawer-items');
      if (itemsEl) itemsEl.classList.remove('is-empty');

      if (parsedState?.sections && typeof cart.renderContents === 'function') {
        // Guard against missing section HTML breaking trapFocus
        const targets = cart.getSectionsToRender();
        const usable = targets.every((section) => {
          const html = parsedState.sections[section.id];
          if (!html) return false;
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const source = doc.querySelector(section.selector || '.shopify-section');
          return Boolean(source);
        });

        if (usable) {
          cart.renderContents(parsedState);
          return true;
        }
      }

      // Manual safe refresh
      if (parsedState?.sections) {
        const targets = cart.getSectionsToRender();
        targets.forEach((section) => {
          const html = parsedState.sections[section.id];
          if (!html) return;
          const sectionElement = section.selector
            ? document.querySelector(section.selector)
            : document.getElementById(section.id);
          if (!sectionElement) return;
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const source = doc.querySelector(section.selector || '.shopify-section');
          if (!source) return;
          sectionElement.innerHTML = source.innerHTML;
        });
      }

      if (document.getElementById('CartDrawer') && typeof cart.open === 'function') {
        const overlay = cart.querySelector('#CartDrawer-Overlay');
        if (overlay) overlay.addEventListener('click', cart.close.bind(cart));
        cart.open();
        return true;
      }
    } catch (error) {
      console.warn('[BGC PageFly options] Cart UI refresh skipped:', error);
    }
    return false;
  }

  async function refreshCartUi(cart, preferredState) {
    if (!cart) {
      if (window.routes?.cart_url) window.location = window.routes.cart_url;
      return;
    }

    if (preferredState?.sections && renderCartLikeDawn(cart, preferredState)) return;

    const sectionIds =
      typeof cart.getSectionsToRender === 'function'
        ? cart.getSectionsToRender().map((section) => section.id)
        : ['cart-drawer', 'cart-icon-bubble'];

    const sections = await fetchCartSections(sectionIds);
    if (sections && renderCartLikeDawn(cart, { sections })) return;

    cart.classList.remove('is-empty');
    if (typeof cart.open === 'function' && document.getElementById('CartDrawer')) {
      cart.open();
    }
  }

  async function addLinkedItems(button) {
    const mainId = resolveVariantId();
    if (!mainId) return;

    const cart = getCartDrawer();
    const originalText = button.textContent;
    button.setAttribute('aria-disabled', 'true');
    button.dataset.bgcBusy = '1';
    if (button.getAttribute('data-adding')) button.textContent = button.getAttribute('data-adding');
    showAtcMessage(button, '', false);

    const linkId =
      (window.BGCLinkedFees && typeof window.BGCLinkedFees.createLinkId === 'function'
        ? window.BGCLinkedFees.createLinkId()
        : 'bgc-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 9));

    const { items: addonItems, unresolved } = await getSelectedAddonItems(config.productTitle || '', linkId);
    const mainItem = {
      id: Number(mainId),
      quantity: resolveQuantity(),
      properties: buildParentProperties(addonItems.length ? linkId : null),
    };
    const warnings = [];
    if (unresolved.length) {
          warnings.push(
            'Add-on price could not be applied for: ' +
              unresolved.join(', ') +
              '. Confirm the club-option-fees product has a variant at that exact price.'
          );
    }

    let mainAdded = false;
    let lastState = null;

    try {
      const mainResult = await addItemWithOptionalSections(mainItem, addonItems.length === 0);
      if (mainResult.data.status || !mainResult.response.ok) {
        showAtcMessage(
          button,
          mainResult.data.description || mainResult.data.message || 'Could not add this product to the cart.',
          true
        );
        button.textContent = originalText;
        return;
      }
      mainAdded = true;
      lastState = mainResult.data;

      for (let i = 0; i < addonItems.length; i++) {
        const isLast = i === addonItems.length - 1;
        const addonResult = await addItemWithOptionalSections(addonItems[i], isLast);
        if (addonResult.data.status || !addonResult.response.ok) {
          const detail =
            addonResult.data.description || addonResult.data.message || 'option could not be added';
          const label =
            (addonItems[i].properties &&
              (addonItems[i].properties.Option ||
                addonItems[i].properties['Extend Club'] ||
                addonItems[i].properties.Regrip)) ||
            'Selected option';
          warnings.push(`${label}: ${detail}`);
        } else {
          lastState = addonResult.data;
        }
      }

      await refreshCartUi(cart, lastState);

      if (warnings.length) {
        showAtcMessage(button, warnings.join(' '), true);
        button.textContent = originalText;
      } else if (button.getAttribute('data-added')) {
        button.textContent = button.getAttribute('data-added');
        setTimeout(() => {
          button.textContent = originalText;
        }, 1200);
      } else {
        button.textContent = originalText;
      }
    } catch (error) {
      console.error('[BGC PageFly options]', error);
      showAtcMessage(
        button,
        mainAdded
          ? 'Product added, but the cart could not be refreshed. Open the cart to confirm.'
          : 'Could not add this product to the cart.',
        true
      );
      button.textContent = originalText;
      if (mainAdded) {
        try {
          await refreshCartUi(cart, lastState);
        } catch (e) {
          /* ignore */
        }
      }
    } finally {
      button.removeAttribute('aria-disabled');
      delete button.dataset.bgcBusy;
    }
  }

  function interceptPageflyAtc() {
    document.addEventListener(
      'click',
      function (event) {
        const button = event.target.closest('[data-pf-type="ProductATC2"]');
        if (!button) return;
        if (!document.querySelector('[data-bgc-source="pagefly"] .bgc-addon-select')) return;
        if (button.dataset.bgcBusy === '1') {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        addLinkedItems(button);
      },
      true
    );
  }

  function injectPicker() {
    const groups = normalizeGroups(config.pricingMap);
    if (!groups.length) return false;

    if (document.querySelector('[data-bgc-source="pagefly"]')) {
      hideHulkOptions(config.productId);
      return true;
    }

    const anchor = findInsertAnchor(config.productId);
    if (!anchor || !anchor.parentNode) return false;

    const host = document.createElement('div');
    host.id = 'bgc-option-addons-mount-' + config.productId;
    host.className = 'bgc-option-addons-mount';
    host.innerHTML = buildPickerHtml(groups, config.productId, config.productTitle || '');

    const hulkMount = findHulkMount(config.productId);
    if (hulkMount && hulkMount.parentNode) {
      hulkMount.parentNode.insertBefore(host, hulkMount);
    } else {
      anchor.parentNode.insertBefore(host, anchor);
    }

    const node = host.querySelector('[data-bgc-source="pagefly"]');
    if (node) {
      node.addEventListener('change', function (event) {
        if (event.target.classList.contains('bgc-addon-select')) updateExtraPrice();
      });
    }

    hideHulkOptions(config.productId);
    watchHulkRerender(config.productId);
    watchBasePriceChanges();
    updateExtraPrice();
    return true;
  }

  function watchBasePriceChanges() {
    if (window.__BGCBasePriceObserver) return;

    const priceNode =
      document.querySelector('[data-pf-type="ProductPrice2Item"][data-product-price="true"]') ||
      document.querySelector('[data-product-type="price"]') ||
      document.querySelector('[data-pf-type="ProductPrice2"]');

    if (priceNode) {
      window.__BGCBasePriceObserver = new MutationObserver(function () {
        updateExtraPrice();
      });
      window.__BGCBasePriceObserver.observe(priceNode, {
        childList: true,
        characterData: true,
        subtree: true,
      });
    }

    // Also refresh when PageFly variant UI changes selection
    document.addEventListener(
      'change',
      function (event) {
        if (!event.target.closest('.pf-product-form, [data-pf-type="ProductBox"]')) return;
        if (event.target.classList.contains('bgc-addon-select')) return;
        updateExtraPrice();
      },
      true
    );
  }

  function start() {
    let attempts = 0;
    const maxAttempts = 40;

    const tryInject = function () {
      attempts += 1;
      if (injectPicker()) {
        interceptPageflyAtc();
        return;
      }
      if (attempts < maxAttempts) {
        setTimeout(tryInject, 250);
      }
    };

    tryInject();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
